import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  IconButton,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useLoginAdmin } from '../utils/auth.api';
import Header from '../Components/Header';
import { toast } from 'react-toastify';
import VehicleDetailsForm from '../Components/VehicleDetailsForm';
import { setTokenWithExpiry } from '../utils/tokenManager';

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCarMessage, setShowCarMessage] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [storedMedia, setStoredMedia] = useState(null);
  const navigate = useNavigate();
  // const { userLoginAdmin } = useLoginAdmin();
  const userLoginMutation = useLoginAdmin();

  // Check for email from localStorage (set by VehicleDetailsForm)
  useEffect(() => {
    const storedEmail = localStorage.getItem('loginEmail');
    if (storedEmail) {
      setEmail(storedEmail);
      // Clear the stored email after using it
      localStorage.removeItem('loginEmail');
    }
  }, []);

  // Check if user navigated from car page and has stored media
  useEffect(() => {
    const navigateFromCar = localStorage.getItem('navigateFromCar');
    if (navigateFromCar === 'true') {
      setShowCarMessage(false);
      
      // Check for stored media
      const storedImage = localStorage.getItem('capturedImages');
      const storedAudio = localStorage.getItem('recordedAudio');
      
      if (storedImage || storedAudio) {
        const mediaData = {
          image: storedImage ? JSON.parse(storedImage) : null,
          audio: storedAudio ? JSON.parse(storedAudio) : null
        };
        setStoredMedia(mediaData);
        console.log('Found stored media:', mediaData);

        // Show contextual toast for media
        if (mediaData.image && !mediaData.audio) {
          toast.info('Image uploaded. Please log in to proceed', { autoClose: 3000 });
        } else if (mediaData.audio && !mediaData.image) {
          toast.info('Audio uploaded. Please log in to proceed', { autoClose: 3000 });
        } else if (mediaData.audio && mediaData.image) {
          toast.info('Media uploaded. Please log in to proceed', { autoClose: 3000 });
        }
      } else {
        // Generic toast if no media found
        toast.info('Please login to upload images or audio.', { autoClose: 3000 });
      }
      
      // Clear the flag after showing the message
      localStorage.removeItem('navigateFromCar');
    }
  }, []);

  const handleSubmit = async (e) => {
    console.log("handleSubmit");
    e.preventDefault();
    if (!email || !password) {
      alert("Please enter email and password");
      return;
    }
    try {
      setIsLoading(true);
      // Call the userLoginAdmin API
      const response = await userLoginMutation.mutateAsync({
        email: email,
        password: password
      });

      // Store login response in localStorage (using keys that match existing components)
      setTokenWithExpiry(response.access_token);
      localStorage.setItem('username', response.username);
      localStorage.setItem('userid', response.userid);
      localStorage.setItem('email', response.email);

      
      // setUserName('');
      setEmail('');
      setPassword('');
      
      // Show success message from API response or default message
      const successMessage = response?.message || 'Login successful';
      toast.success(successMessage);
      
      // If we have stored media, show vehicle form instead of navigating to dashboard
      if (storedMedia) {
        setShowVehicleForm(true);
        console.log('Showing vehicle form for stored media:', storedMedia);
      } else {
        navigate('/dashboard');
      }

    } catch (error) {
      console.error("Login failed:", error);
      
      // Extract error message from API response
      let errorMessage = "Login failed. Please check your credentials.";
      
      if (error?.response?.data?.detail) {
        // If API returns error in the format { detail: "error message" }
        errorMessage = error.response.data.detail;
      } else if (error?.response?.data?.message) {
        // If API returns error in the format { message: "error message" }
        errorMessage = error.response.data.message;
      } else if (error?.detail) {
        // If error object has detail property directly
        errorMessage = error.detail;
      } else if (error?.message) {
        // Handle other error formats
        if (error.message.includes('Unexpected response')) {
          errorMessage = "Server response format is unexpected. Please contact support.";
        } else if (error.message.includes('Network') || error.message.includes('fetch')) {
          errorMessage = "Network error. Please check your internet connection.";
        } else {
          errorMessage = error.message;
        }
      }
      
      // Show error message from API
      toast.error(errorMessage);
      console.error("Detailed error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle vehicle form completion
  const handleVehicleFormComplete = () => {
    setShowVehicleForm(false);
    setStoredMedia(null);
    navigate('/dashboard');
  };

  // Handle vehicle form close
  const handleVehicleFormClose = () => {
    setShowVehicleForm(false);
    setStoredMedia(null);
    navigate('/dashboard');
  };

  // Listen for vehicle creation events
  useEffect(() => {
    const handleVehicleCreated = (event) => {
      const { vehicle, fromStoredMediaFlow } = event.detail;
      
      if (fromStoredMediaFlow) {
        console.log('🚗 Vehicle created from stored media flow:', vehicle);
        
        // Store the newly created vehicle info for auto-selection
        localStorage.setItem('newlyCreatedVehicle', JSON.stringify(vehicle));
        localStorage.setItem('autoSelectNewVehicle', 'true');
        
        // Close the form and navigate to dashboard
        setShowVehicleForm(false);
        setStoredMedia(null);
        navigate('/dashboard');
      }
    };

    window.addEventListener('vehicleCreated', handleVehicleCreated);
    
    return () => {
      window.removeEventListener('vehicleCreated', handleVehicleCreated);
    };
  }, [navigate]);

  return (
    <Box minH="100vh" bg="white">
      <Header hasToken={!!localStorage.getItem('token')} onMenuClick={() => {}} />
      <Container maxW="sm" centerContent>
        <VStack spacing={8} w="100%" pt={12}>
          
          {/* Toasts are shown via react-toastify; no inline alert needed */}

<Box textAlign="left" w="100%">
  <Heading as="h1" size="lg" color="gray.900" fontWeight="bold" lineHeight="1.2">
    Login to your
  </Heading>
  <Heading as="h1" size="lg" color="gray.900" fontWeight="bold" lineHeight="1.2">
    account.
  </Heading>
</Box>
<Text color="gray.500" w="100%" textAlign="left" mt={-5}>
  Please sign in to your account
</Text>
          <VStack as="form" spacing={6} w="100%" onSubmit={handleSubmit}>
            <FormControl>
              <FormLabel>Email Address</FormLabel>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                size="lg"
                bg="white"
                borderColor="gray.300"
                isDisabled={isLoading}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Password</FormLabel>
              <InputGroup>
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  size="lg"
                  bg="white"
                  borderColor="gray.300"
                  isDisabled={isLoading}
                />
                <InputRightElement w="3rem" h="100%">
  <IconButton
    variant="ghost"
    onClick={() => setShowPassword(!showPassword)}
    // icon={showPassword ? <ViewOffIcon fontSize="lg" /> : <ViewIcon fontSize="lg" />}
    icon={showPassword ? <ViewIcon fontSize="lg" /> : <ViewOffIcon fontSize="lg" />}

    size="sm"
    aria-label={showPassword ? "Hide password" : "Show password"}
    minW="auto"
    h="auto"
  />
</InputRightElement>
              </InputGroup>
            </FormControl>
            <Box w="100%" textAlign="right">
              <Text
                color="#FE0000"
                fontFamily="'Inter', sans-serif"
                fontWeight={500}
                fontSize="14px"
                lineHeight="20px"
                letterSpacing="0"
                cursor="pointer"
                onClick={() => navigate('/forget-password')}
              >
                Forgot password?
              </Text>
            </Box>
            <Button
              type="submit"
              w="100%"
              bg="#5A5BED"
              height='50px'
              color="white"
              fontWeight="semibold"
              borderRadius="100px"
              isLoading={isLoading}
              loadingText="Signing In..."
              spinner={<Spinner size="sm" color="white" />}
            >
              Sign In
            </Button>
          </VStack>
          {/* Desktop: show in normal flow */}
          <Box
            display={{ base: "none", md: "block" }}
            pt={4}
            w="100%"
          >
            <Text color="gray.600" textAlign="center">
              Don't have an account?{' '}
              <span 
                style={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate('/sign-up')}
              >
                Register
              </span>
            </Text>
          </Box>

          {/* Mobile: fix to bottom */}
          <Box
            display={{ base: "block", md: "none" }}
            position="fixed"
            bottom="0"
            left="0"
            w="100vw"
            bg="white"
            py={4}
            boxShadow="0 -2px 8px rgba(0,0,0,0.03)"
            zIndex={10}
          >
            <Text color="gray.600" textAlign="center">
              Don't have an account?{' '}
              <span 
                style={{ color: '#3b82f6', textDecoration: 'underline', cursor: 'pointer' }}
                onClick={() => navigate('/sign-up')}
              >
                Register
              </span>
            </Text>
          </Box>
        </VStack>
      </Container>

      {/* Vehicle Details Form Modal */}
      {showVehicleForm && (
        <VehicleDetailsForm
          isOpen={showVehicleForm}
          onClose={handleVehicleFormClose}
          onEditComplete={handleVehicleFormComplete}
          storedMedia={storedMedia}
        />
      )}
    </Box>
  );
};

export default LoginPage;